import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/ecommerce-development")({
  head: () => {
    const h = pageHead("shopEntwicklung", "en");
    return { meta: h.meta, links: h.links };
  },
  component: ShopPage,
});

const steps = [
  { num: "01", title: "Business model & data model", body: "We clarify assortment, pricing logic, target groups and sales channels — and how product data must be structured so it runs cleanly everywhere." },
  { num: "02", title: "Store & checkout", body: "Building the store system with a focus on a smooth, conversion-strong checkout, performant category and product pages, and a maintainable technical base." },
  { num: "03", title: "Integrations & connectivity", body: "Connecting payment, shipping, inventory management and ERP, plus e-commerce tracking, product feeds and marketing requirements — structurally, not as an afterthought." },
  { num: "04", title: "Scaling & maintenance", body: "After go-live we support assortment expansion, performance, new channels and ongoing technical maintenance." },
];

const capabilities = [
  { name: "Checkout optimization", detail: "short, clear, conversion-strong" },
  { name: "Payment integration", detail: "payment methods, taxes, invoicing logic" },
  { name: "Product data & feeds", detail: "structured, exportable, marketing-ready" },
  { name: "ERP & shipping integrations", detail: "automated instead of manual" },
  { name: "E-commerce tracking", detail: "purchase events, remarketing" },
  { name: "Performance optimization", detail: "load times even with a large assortment" },
  { name: "Internationalization", detail: "languages, currencies, tax rules" },
  { name: "Scaling & maintenance", detail: "growing with the assortment" },
];

const stack = [
  { name: "Shopware", small: "e-commerce, DACH" },
  { name: "Shopify", small: "e-commerce, fast to launch", href: "/en/services/ecommerce-development/shopify-agency", ariaLabel: "More about our Shopify agency" },
  { name: "WooCommerce", small: "flexible, WordPress-based" },
];

const mechanics = [
  { title: "Checkout & conversion", body: "Every unnecessary step costs revenue. We build checkouts short, clear and trustworthy." },
  { title: "Payment & taxes", body: "Payment methods, tax rules, invoicing logic and country-specific requirements cleanly integrated." },
  { title: "Product data & PIM", body: "Product data lives in a system, not in spreadsheets — structured, exportable, usable for both store and marketing alike.", link: { label: "Linked tool: Delta →", href: "/en/services/delta" } },
  { title: "Integrations", body: "ERP, inventory management, shipping providers and accounting mesh automatically, instead of being maintained by hand." },
  { title: "E-commerce tracking", body: "Purchase events, product feeds and dynamic remarketing anchored structurally from the start — the basis for profitable performance marketing." },
  { title: "Legal compliance", body: "Right of withdrawal, price indication requirements and mandatory disclosures correctly implemented." },
];

function ShopPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="E-Commerce Development"
          headline={<>An online store isn't <em>a website with a checkout button.</em> It's a selling system.</>}
          lead="We build online stores on Shopware, Shopify and WooCommerce that don't just display products — they sell, with a clean checkout, reliable integrations and well-maintained product data as the foundation. Built for merchants who treat their store as a revenue engine, not a digital catalogue."
          metaStrip={[
            "Shopware · Shopify · WooCommerce",
            "PIM & feed management",
            "ERP & shipping integration",
            "E-commerce tracking as standard",
          ]}
          primaryCta={{
            eyebrow: "Free · approx. 30 minutes · no obligation",
            label: "Request a strategy conversation",
            to: "/en/contact",
          }}
          secondaryCta={{
            label: "Or: download the e-commerce checklist without signup",
            to: "/en/contact",
          }}
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "E-Commerce Development" }]}
        />


        <Section number="02" kicker="Our position" headline={<>An online store rarely fails on design. It fails <em>on mechanics.</em></>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Need a website without a sales function? That's a separate discipline —{" "}
              <a href="/en/services/website-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                go to Website Development →
              </a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">E-commerce carries a complexity a classic website never has. The moment money, stock and logistics are involved, success isn't decided by looks but by the mechanics underneath: How clean is the checkout? How reliably do stock levels, orders and prices flow between the store, inventory management and accounting? How well is product data maintained and usable for marketing?</p>
              <p className="mb-5">In practice we keep seeing the same brakes: a checkout with too many steps and drop-offs, product data living in spreadsheets instead of a system, ERP and shipping integrations that have to be maintained by hand, missing or patchy e-commerce tracking, and a store that doesn't scale with the assortment. The result: revenue is left on the table that never becomes visible.</p>
              <p>We build online stores as selling systems. It starts not with the product page, but with the business model: how you sell, to whom, through which channels, with what logistics and which systems in the background. Only then do we build.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Our approach" headline={<>Four phases from the <em>business model</em> to a running store.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="04" kicker="Mechanics" headline={<>What makes an online store <em>a system.</em></>} caption="The things that decide success — and that a website doesn't have.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {mechanics.map((m) => (
                <div key={m.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{m.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{m.body}</p>
                  {m.link && (
                    <a href={m.link.href} className="mt-4 inline-flex items-center font-mono text-xs uppercase tracking-wider text-ink-2 hover:text-lime-deep">
                      {m.link.label}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="05" kicker="Capabilities & stack" headline={<>How we make online stores <em>built to sell.</em></>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="06" />

        <RelatedServices
          locale="en"
          fromId="shop-entwicklung"
          number="07"
          items={relatedBySource["shop-entwicklung"]}
        />

        <Section number="08" kicker="Engagement">
          <CTABlock
            headline={<>Let's talk about your <em>online store.</em></>}
            body="Whether a new store, a replatforming or an existing store that should do more — in a strategy call we clarify what fits your assortment, your logistics and your business model. Concrete, honest and with no sales pressure."
            primary={{ label: "Request a strategy call", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
